import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { useUnidadesHabitadas } from '@/features/auth/use-unidades-habitadas';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { MisPasesContent } from './mis-pases-content';
import { MiUnidadTab } from './mi-unidad-tab';

type TabKey = 'pases' | 'unidad';

export function MyPasses() {
  const { condoId } = useCondoContext();
  const { data: unidades, isLoading } = useUnidadesHabitadas();
  const habitaAlguna = (unidades ?? []).length > 0;
  const [tab, setTab] = useState<TabKey>('pases');

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Mis visitas"
        title="Control de visitas"
        subtitle="Tus pases QR y el historial de visitas de tu unidad."
        action={
          habitaAlguna ? (
            <Button asChild>
              <Link to={`/app/condominios/${condoId}/visitas/crear`}>
                <Plus className="h-4 w-4" />
                Crear pase
              </Link>
            </Button>
          ) : (
            <Button disabled title="Debes habitar una unidad para crear pases">
              <Plus className="h-4 w-4" />
              Crear pase
            </Button>
          )
        }
      />

      <div className="flex gap-2 border-b border-[var(--color-hairline)] pb-2 mb-6">
        <TabButton active={tab === 'pases'} onClick={() => setTab('pases')}>
          Mis pases QR
        </TabButton>
        {habitaAlguna && (
          <TabButton active={tab === 'unidad'} onClick={() => setTab('unidad')}>
            Mi unidad
          </TabButton>
        )}
      </div>

      {tab === 'pases' && <MisPasesContent />}
      {tab === 'unidad' && habitaAlguna && <MiUnidadTab unidades={unidades ?? []} />}

      {!isLoading && !habitaAlguna && tab === 'pases' && (
        <p className="mt-6 text-sm text-[var(--color-ink-muted)]">
          Para crear pases y ver el historial de visitas debes habitar una
          unidad. Contacta al administrador si crees que es un error.
        </p>
      )}
    </PageContainer>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm transition-colors ${
        active
          ? 'text-[var(--color-ink)] border-b-2 border-[var(--color-accent)] -mb-[9px]'
          : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
      }`}
    >
      {children}
    </button>
  );
}
